<template>
  <!-- Fiche de fin d'appel — ancrée dans le panneau téléphone, à la place
       du composeur (cadran + pavé). Elle ne recouvre plus l'écran :
       l'espace de travail (fiche contact, formulaires, RDV, notes) reste
       visible et éditable jusqu'à la clôture. -->
  <transition name="dispo">
    <div v-if="callStore.showDisposition" class="dispo">
      <header class="head">
        <h2>Fin d'appel</h2>
        <p class="sub">
          <b>{{ entry?.name || 'Sans nom' }}</b> · {{ entry?.phone }}
        </p>
      </header>

      <div class="body">
        <!-- Choix mode -->
        <div v-if="mode === 'choose'" class="choice-grid">
          <button class="choice" @click="mode = 'complete'">
            <Icon name="check-circle" :size="20" class="ci ci-ok" />
            <div class="ci-title">Terminer</div>
            <div class="ci-desc">Marquer comme traité</div>
          </button>
          <button class="choice" @click="mode = 'callback'">
            <Icon name="clock" :size="20" class="ci ci-warn" />
            <div class="ci-title">Rappeler</div>
            <div class="ci-desc">Programmer un rappel</div>
          </button>
          <!-- Toujours présente : met le numéro en liste noire (ne plus jamais
               appeler) et clôture la fiche. -->
          <button class="choice choice-danger" @click="submitBlacklist" :disabled="loading">
            <Icon name="x-circle" :size="20" class="ci ci-danger" />
            <div class="ci-title">Liste noire</div>
            <div class="ci-desc">Ne plus jamais appeler</div>
          </button>
        </div>

        <!-- Complete -->
        <div v-else-if="mode === 'complete'" class="form">
          <label class="lbl">Résultat de l'appel</label>
          <!-- Dispositions de la campagne (boutons colorés). Repli sur les
               outcomes figés si la campagne n'en a pas (campagnes legacy). -->
          <div v-if="dispositions.length" class="dispo-grid">
            <button
              v-for="d in dispositions"
              :key="d.id"
              type="button"
              class="dispo-btn"
              :class="{ active: selectedDisposition?.id === d.id }"
              :style="selectedDisposition?.id === d.id ? { borderColor: d.color, background: d.color + '18' } : {}"
              :title="d.description"
              @click="pickDisposition(d)"
            >
              <span class="dispo-dot" :style="{ background: d.color }"></span>
              <span class="dispo-btn-title">{{ d.title }}</span>
            </button>
          </div>
          <div v-else class="outcomes">
            <button
              v-for="(label, key) in OUTCOME_LABELS"
              :key="key"
              type="button"
              class="outcome"
              :class="{ active: outcome === key }"
              @click="outcome = key"
            >
              {{ label }}
            </button>
          </div>

          <!-- Formulaire de VENTE : auto-affiché quand la disposition compte
               comme une vente (counts_as_sale). Montant + champs du formulaire. -->
          <div v-if="selectedDisposition?.counts_as_sale" class="sale-box">
            <div class="sale-box-head"><Icon name="briefcase" :size="13" /> Détails de la vente</div>
            <label class="lbl" for="sale-amount">Montant</label>
            <input id="sale-amount" v-model="saleAmount" type="number" step="0.01" min="0" class="text-input sale-amount" placeholder="0.00" />
            <template v-for="f in (saleForm?.fields || [])" :key="f.key">
              <label class="lbl">{{ f.label }}<span v-if="f.required" class="req">*</span></label>
              <textarea v-if="f.field_type === 'textarea'" v-model="saleValues[f.key]" rows="2" class="text-input" />
              <select v-else-if="f.field_type === 'select'" v-model="saleValues[f.key]" class="text-input">
                <option value="">—</option>
                <option v-for="o in f.options" :key="o" :value="o">{{ o }}</option>
              </select>
              <label v-else-if="f.field_type === 'checkbox'" class="cf-check">
                <input type="checkbox" v-model="saleValues[f.key]" /> <span>{{ f.help_text || 'Oui' }}</span>
              </label>
              <input v-else v-model="saleValues[f.key]" :type="saleInputType(f.field_type)" class="text-input" />
            </template>
          </div>

          <label class="lbl" for="service-input">
            Service ou besoin <span class="opt">(optionnel)</span>
          </label>
          <input
            id="service-input"
            v-model="serviceRequested"
            type="text"
            class="text-input"
            list="service-suggestions"
            placeholder="Ex : Demande de devis, prise de RDV…"
            maxlength="200"
          />
          <datalist id="service-suggestions">
            <option value="Demande d'information" />
            <option value="Prise de rendez-vous" />
            <option value="Demande de devis" />
            <option value="Réclamation" />
            <option value="Support technique" />
            <option value="Suivi de dossier" />
          </datalist>

          <label class="lbl">Va-t-il rappeler ?</label>
          <div class="seg">
            <button
              v-for="(lbl, key) in WILL_CALLBACK_LABELS"
              :key="key"
              type="button"
              class="seg-item"
              :class="{ active: willCallback === key }"
              @click="willCallback = (willCallback === key ? '' : key) as WillCallback"
            >
              {{ lbl }}
            </button>
          </div>

          <!-- Champs personnalisés : en mode normal ils s'éditent dans la
               fiche d'appel restée ouverte à gauche ; en mini softphone le
               contenu est masqué → on réaffiche les champs ici (mêmes
               valeurs, saisie partagée via le store). -->
          <div v-if="isCompact && ficheLive && callStore.customFields.length" class="custom-fields">
            <div class="cf-title">Fiche</div>
            <div v-for="f in callStore.customFields" :key="f.id" class="cf">
              <label class="lbl">
                {{ f.label }}
                <span v-if="f.required && f.editable_by_agent" class="req">*</span>
                <span v-else-if="!f.editable_by_agent" class="opt">(lecture seule)</span>
              </label>

              <template v-if="f.field_type === 'checkbox'">
                <label class="cf-check">
                  <input type="checkbox" v-model="callStore.fieldValues[f.key]" :disabled="!f.editable_by_agent" />
                  <span>{{ f.help_text || 'Oui' }}</span>
                </label>
              </template>
              <textarea
                v-else-if="f.field_type === 'textarea'"
                v-model="callStore.fieldValues[f.key]"
                rows="2"
                :disabled="!f.editable_by_agent"
                :placeholder="f.help_text"
              />
              <select
                v-else-if="f.field_type === 'select'"
                v-model="callStore.fieldValues[f.key]"
                :disabled="!f.editable_by_agent"
                class="text-input"
              >
                <option value="">—</option>
                <option v-for="o in f.options" :key="o" :value="o">{{ o }}</option>
              </select>
              <input
                v-else
                v-model="callStore.fieldValues[f.key]"
                :type="fieldInputType(f.field_type)"
                class="text-input"
                :disabled="!f.editable_by_agent"
                :placeholder="f.help_text"
              />
            </div>
          </div>
          <p v-else-if="requiredMissing" class="hint warn">
            <Icon name="alert-circle" :size="12" />
            Des champs requis de la fiche restent à remplir — complétez-les dans la fiche d'appel.
          </p>

          <label class="lbl">Notes <span class="opt">(optionnel)</span></label>
          <textarea
            v-model="callStore.callNote"
            rows="3"
            placeholder="Commentaires sur l'appel..."
          ></textarea>

          <!-- Rendez-vous : le formulaire se remplit dans la fiche d'appel
               (dès pendant l'appel) ; l'interrupteur reste accessible ici. -->
          <div v-if="ficheLive && callStore.apptForm" class="appt">
            <div class="appt-head">
              <div>
                <div class="appt-title">Rendez-vous obtenu ?</div>
                <div class="appt-sub">{{ callStore.apptForm.name }}</div>
              </div>
              <ToggleSwitch v-model="callStore.apptObtained" />
            </div>
            <div v-if="callStore.apptObtained && isCompact" class="appt-fields">
              <div v-for="f in callStore.apptForm.fields" :key="f.id" class="cf">
                <label class="lbl">{{ f.label }} <span v-if="f.required" class="req">*</span></label>
                <label v-if="f.field_type === 'checkbox'" class="cf-check">
                  <input type="checkbox" v-model="callStore.apptValues[f.key]" /><span>{{ f.help_text || 'Oui' }}</span>
                </label>
                <textarea v-else-if="f.field_type === 'textarea'" v-model="callStore.apptValues[f.key]" rows="2" :placeholder="f.help_text" />
                <select v-else-if="f.field_type === 'select'" v-model="callStore.apptValues[f.key]" class="text-input">
                  <option value="">—</option>
                  <option v-for="o in f.options" :key="o" :value="o">{{ o }}</option>
                </select>
                <input v-else v-model="callStore.apptValues[f.key]" :type="fieldInputType(f.field_type)" class="text-input" :placeholder="f.help_text" />
              </div>
            </div>
            <p v-else-if="callStore.apptObtained" class="hint" :class="{ warn: apptRequiredMissing }">
              <Icon :name="apptRequiredMissing ? 'alert-circle' : 'check'" :size="12" />
              <template v-if="apptRequiredMissing">
                Champs requis du rendez-vous à compléter dans la fiche d'appel.
              </template>
              <template v-else>Formulaire repris depuis la fiche d'appel.</template>
            </p>
          </div>

          <!-- Partenaire PexiLine : obligatoire dès que le compte est rattaché à
               PexiLine et qu'un RDV va être créé. « Sans partenaire » laisse
               PexiLine distribuer librement ; un partenaire choisi verrouille
               l'assignation sur lui (sauf dérogation d'un admin PexiLine). -->
          <p v-if="apptWillBeCreated && partnersUnavailable" class="hint warn">
            <Icon name="alert-circle" :size="12" /> Liste des partenaires PexiLine indisponible : le RDV partira « sans partenaire ».
          </p>
          <div v-if="apptWillBeCreated && pexilinePartners.length" class="cf appt-partner">
            <label class="lbl">Partenaire PexiLine <span class="req">*</span></label>
            <select v-model="callStore.pexilinePartnerId" class="text-input">
              <option value="" disabled>Choisir un partenaire…</option>
              <option value="none">Sans partenaire</option>
              <option v-for="p in pexilinePartners" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
            <p v-if="partnerChoiceMissing" class="hint warn">
              <Icon name="alert-circle" :size="12" /> Choisissez un partenaire ou « Sans partenaire ».
            </p>
          </div>

          <div class="actions">
            <button class="btn-primary" @click="submitComplete(false)" :disabled="!hasResult || loading || requiredMissing || apptRequiredMissing || saleRequiredMissing || partnerChoiceMissing">
              <Icon name="check" :size="14" />
              <span>{{ loading ? 'Enregistrement...' : (dialer.isActive ? 'Terminer → appel suivant' : 'Terminer l\'appel') }}</span>
            </button>
            <!-- Session de numérotation active : l'agent choisit entre
                 enchaîner sur le prochain numéro ou arrêter le défilement. -->
            <button
              v-if="dialer.isActive"
              type="button"
              class="btn-stop-session"
              @click="submitComplete(true)"
              :disabled="!hasResult || loading || requiredMissing || apptRequiredMissing || saleRequiredMissing || partnerChoiceMissing"
              title="Enregistrer la fiche et arrêter le défilement automatique"
            >
              <Icon name="square" :size="13" />
              <span>Terminer et arrêter</span>
            </button>
            <button type="button" class="btn-text" @click="mode = 'choose'">
              <Icon name="chevron-left" :size="14" /> Retour
            </button>
          </div>

          <!-- Liste noire propre à la campagne — n'a rien à voir avec la liste
               noire globale. Le numéro ne sera plus appelé DANS cette campagne. -->
          <button
            v-if="entry?.campaign"
            type="button"
            class="btn-blacklist-camp"
            :disabled="loading"
            @click="blacklistCampaign"
          >
            <Icon name="x-circle" :size="13" />
            <span>Liste noire de cette campagne</span>
          </button>
        </div>

        <!-- Callback -->
        <div v-else-if="mode === 'callback'" class="form">
          <label class="lbl">Date et heure</label>
          <div class="dt">
            <input v-model="cbDate" type="date" :min="todayStr" />
            <input v-model="cbTime" type="time" />
          </div>

          <div class="quick">
            <button @click="setQuickTime(15)">+15 min</button>
            <button @click="setQuickTime(60)">+1 h</button>
            <button @click="setQuickTime(1440)">Demain</button>
            <button @click="setQuickTime(4320)">+3 jours</button>
          </div>

          <label class="lbl">Note <span class="opt">(optionnel)</span></label>
          <textarea v-model="cbNote" rows="3" placeholder="Pourquoi rappeler ?"></textarea>

          <div class="actions">
            <button class="btn-primary" @click="submitCallback(false)" :disabled="!cbDate || !cbTime || loading">
              <Icon name="clock" :size="14" />
              <span>{{ loading ? 'Enregistrement...' : (dialer.isActive ? 'Programmer → appel suivant' : 'Programmer') }}</span>
            </button>
            <button
              v-if="dialer.isActive"
              type="button"
              class="btn-stop-session"
              @click="submitCallback(true)"
              :disabled="!cbDate || !cbTime || loading"
              title="Programmer le rappel et arrêter le défilement automatique"
            >
              <Icon name="square" :size="13" />
              <span>Programmer et arrêter</span>
            </button>
            <button class="btn-text" @click="mode = 'choose'">
              <Icon name="chevron-left" :size="14" /> Retour
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import Icon from '@/shared/components/Icon.vue'
import ToggleSwitch from '@/shared/components/ToggleSwitch.vue'
import { useActiveCallStore } from '@/shared/stores/active_call'
import { QueueAPI, DispositionAPI, OUTCOME_LABELS, type Outcome, type WillCallback, type CampaignDisposition } from '@/shared/api/queue'
import { AppointmentAPI, type FormTemplate, type FormFieldType } from '@/shared/api/forms'

const WILL_CALLBACK_LABELS: Record<Exclude<WillCallback, ''>, string> = {
  yes: 'Oui',
  no: 'Non',
  maybe: 'Peut-être',
}
import { useAgentStatusStore } from '@/shared/stores/agent_status'
import { useDialerSessionStore } from '@/shared/stores/dialer_session'
import { useCompactMode } from '@/shared/composables/useCompactMode'
import { useConfirmStore } from '@/shared/stores/confirm'
import { useToasts } from '@/shared/stores/toasts'

const callStore = useActiveCallStore()
const agentStore = useAgentStatusStore()
const dialer = useDialerSessionStore()
const confirmStore = useConfirmStore()
const toasts = useToasts()
// Mini softphone : le contenu (donc la fiche d'appel) est masqué — les
// champs fiche + RDV sont alors réaffichés ici, branchés sur le store.
const { isCompact } = useCompactMode()

const entry = computed(() => callStore.completedEntry)
const mode = ref<'choose' | 'complete' | 'callback'>('choose')
const outcome = ref<Outcome | ''>('')

// Dispositions de la campagne (parité CloudTalk). Si vide → repli sur OUTCOME_LABELS.
const dispositions = ref<CampaignDisposition[]>([])
const selectedDisposition = ref<CampaignDisposition | null>(null)
// Disposition de type `reschedule` ayant routé vers le mode rappel : on la
// rattache au complete() final pour tracer `last_disposition`.
const rescheduleDisposition = ref<CampaignDisposition | null>(null)

// Au moins un résultat choisi (disposition OU outcome legacy).
const hasResult = computed(() => !!selectedDisposition.value || !!outcome.value)

function pickDisposition(d: CampaignDisposition) {
  if (d.type === 'reschedule') {
    // Type reprogrammer → on bascule vers la saisie de rappel ; la disposition
    // est rattachée au complete() final (cf. submitCallback).
    rescheduleDisposition.value = d
    selectedDisposition.value = null
    mode.value = 'callback'
    return
  }
  selectedDisposition.value = d
}
const willCallback = ref<WillCallback>('')
const serviceRequested = ref('')
const cbDate = ref('')
const cbTime = ref('')
const cbNote = ref('')
const loading = ref(false)

// La fiche partagée (champs campagne + formulaire RDV) appartient-elle bien
// à l'entrée en cours de clôture ? Un second appel (entrant) peut recharger
// le store pour une autre fiche pendant le wrap-up.
const ficheLive = computed(
  () => !!entry.value && callStore.ficheEntryId === entry.value.id,
)

// Un RDV sera créé à la validation (formulaire RDV coché, ou mémo date/heure
// sans formulaire) : le choix du partenaire PexiLine devient obligatoire dès
// que le compte est rattaché à PexiLine (liste non vide).
const apptWillBeCreated = computed(() =>
  ficheLive.value && (
    (callStore.apptObtained && !!callStore.apptForm) ||
    (!callStore.apptForm && !!callStore.plannedAppointmentAt)
  ),
)
const pexilinePartners = ref<Array<{ id: string; name: string }>>([])
// PexiLine injoignable : on le dit plutôt que de laisser croire au compte
// qu'il n'a aucun partenaire (le RDV part alors sans partenaire).
const partnersUnavailable = ref(false)
watch(
  () => callStore.showDisposition,
  (open) => {
    if (!open) return
    AppointmentAPI.labyrdvPartners().then((res) => {
      pexilinePartners.value = res.partners
      partnersUnavailable.value = res.error === true
    })
  },
  { immediate: true },
)
const partnerChoiceMissing = computed(
  () => apptWillBeCreated.value && pexilinePartners.value.length > 0 && !callStore.pexilinePartnerId,
)

// Champs requis éditables encore vides → bloque la validation.
const requiredMissing = computed(() => {
  if (!ficheLive.value) return false
  return callStore.customFields.some((f) => {
    if (!f.required || !f.editable_by_agent) return false
    const v = callStore.fieldValues[f.key]
    return v === undefined || v === null || v === '' || v === false
  })
})

// Champs requis du formulaire RDV non remplis (seulement si RDV obtenu).
const apptRequiredMissing = computed(() => {
  if (!ficheLive.value || !callStore.apptObtained || !callStore.apptForm) return false
  return callStore.apptForm.fields.some((f) => {
    if (!f.required) return false
    const v = callStore.apptValues[f.key]
    return v === undefined || v === null || v === '' || v === false
  })
})

// ── Vente : formulaire inline auto-affiché sur une disposition « vente » ──
const saleForm = ref<FormTemplate | null>(null)
const saleValues = ref<Record<string, any>>({})
const saleAmount = ref('')

watch(selectedDisposition, async (d) => {
  if (!d?.counts_as_sale || !entry.value) return
  try {
    const r = await QueueAPI.saleForm(entry.value.id)
    saleForm.value = r.form
    const init: Record<string, any> = {}
    for (const f of r.form?.fields || []) init[f.key] = f.field_type === 'checkbox' ? false : ''
    saleValues.value = init
  } catch {
    saleForm.value = null
    saleValues.value = {}
  }
})

function saleInputType(t: FormFieldType): string {
  switch (t) {
    case 'number': return 'number'
    case 'date': return 'date'
    case 'time': return 'time'
    case 'datetime': return 'datetime-local'
    case 'email': return 'email'
    case 'phone': return 'tel'
    default: return 'text'
  }
}
// Champs requis du formulaire de vente non remplis (bloque la validation).
const saleRequiredMissing = computed(() => {
  if (!selectedDisposition.value?.counts_as_sale || !saleForm.value) return false
  return saleForm.value.fields.some((f) => {
    if (!f.required) return false
    const v = saleValues.value[f.key]
    return v === undefined || v === null || v === '' || v === false
  })
})

// Dispositions de la campagne du numéro, chargées à l'ouverture.
watch(
  () => [callStore.showDisposition, entry.value?.campaign] as const,
  async ([show, campaignId]) => {
    if (!show) {
      dispositions.value = []
      selectedDisposition.value = null
      rescheduleDisposition.value = null
      return
    }
    if (campaignId) {
      try { dispositions.value = await DispositionAPI.list(campaignId) }
      catch { dispositions.value = [] }
    } else {
      // Appel HORS campagne (fiche manuelle, campaign=null) → jeu de
      // dispositions PAR DÉFAUT du tenant (les 9), au lieu du repli legacy.
      try { dispositions.value = await DispositionAPI.list() }
      catch { dispositions.value = [] }
    }
  },
  { immediate: true },
)

const todayStr = new Date().toISOString().slice(0, 10)

function setQuickTime(minutes: number) {
  const d = new Date(Date.now() + minutes * 60 * 1000)
  cbDate.value = d.toISOString().slice(0, 10)
  cbTime.value = d.toTimeString().slice(0, 5)
}

/** Édits de wrap-up encore non enregistrés (nom, email…) → flush avant la
 *  clôture, sinon ils seraient perdus au reset de la fiche. */
async function flushFiche() {
  if (!ficheLive.value || !callStore.flushFiche) return
  try { await callStore.flushFiche() } catch { /* la clôture reste possible */ }
}

async function blacklistCampaign() {
  if (!entry.value) return
  if (!window.confirm(
    'Mettre ce numéro en liste noire de CETTE campagne ?\n'
    + 'Il ne sera plus appelé dans cette campagne (sans effet sur les autres ni sur la liste noire globale).',
  )) return
  loading.value = true
  try {
    await QueueAPI.blacklist(entry.value.id, 'Liste noire campagne (agent)', 'campaign')
    toasts.success('Liste noire', 'Numéro ajouté à la liste noire de la campagne.')
    await agentStore.change('available').catch(() => {})
    reset()
    callStore.closeDisposition()
    dialer.onCallCompleted()
  } catch (e: any) {
    toasts.error('Échec', e?.response?.data?.detail || 'Réessayez.')
  } finally { loading.value = false }
}

async function submitComplete(stopAfter = false) {
  if (!entry.value || !hasResult.value) return
  loading.value = true
  try {
    await flushFiche()
    // N'envoie que les champs éditables (le serveur re-filtre de toute façon).
    const customData: Record<string, any> = {}
    if (ficheLive.value) {
      for (const f of callStore.customFields) {
        if (f.editable_by_agent) customData[f.key] = callStore.fieldValues[f.key]
      }
    }
    // Rendez-vous obtenu : on joint les valeurs saisies dans la fiche d'appel
    // + l'horodatage déduit d'un champ date/datetime du formulaire.
    let appointment: {
      data?: Record<string, any>
      scheduled_at_local?: string | null
      pexiline_partner?: { id: string; name: string }
    } | undefined
    if (ficheLive.value && callStore.apptObtained && callStore.apptForm) {
      // Avec formulaire RDV : valeurs + heure murale naïve (localisée serveur).
      appointment = { data: { ...callStore.apptValues }, scheduled_at_local: deriveScheduledAt() }
    } else if (ficheLive.value && !callStore.apptForm && callStore.plannedAppointmentAt) {
      // Sans formulaire RDV configuré : le simple mémo date/heure de la fiche
      // suffit à enregistrer le rendez-vous (sinon il serait perdu).
      appointment = { scheduled_at_local: callStore.plannedAppointmentAt }
    }
    // Partenaire PexiLine choisi (« Sans partenaire » = ids vides).
    if (appointment && pexilinePartners.value.length && callStore.pexilinePartnerId) {
      const chosen = pexilinePartners.value.find((c) => c.id === callStore.pexilinePartnerId)
      appointment.pexiline_partner = chosen ? { id: chosen.id, name: chosen.name } : { id: '', name: '' }
    }
    // Vente : disposition « vente » → on joint les valeurs du formulaire + le
    // montant. Présence du payload côté serveur = enregistrement de la Sale.
    let sale: { data?: Record<string, any>; amount?: string } | undefined
    if (selectedDisposition.value?.counts_as_sale) {
      sale = { data: { ...saleValues.value }, amount: saleAmount.value || undefined }
    }
    // Disposition par campagne (prime) OU outcome legacy en repli.
    await QueueAPI.complete(entry.value.id, {
      disposition_id: selectedDisposition.value?.id,
      outcome: selectedDisposition.value ? undefined : (outcome.value as Outcome),
      notes: callStore.callNote,
      will_callback: willCallback.value || undefined,
      service_requested: serviceRequested.value.trim() || undefined,
      custom_data: Object.keys(customData).length ? customData : undefined,
      appointment,
      sale,
    })
    await agentStore.change('available').catch(() => {})
    reset()
    callStore.closeDisposition()
    // Session d'appels active → soit on arrête le défilement (« Terminer et
    // arrêter »), soit on enchaîne sur le numéro suivant (immédiat ou après
    // le compte à rebours de post-appel de la campagne).
    if (stopAfter) dialer.stop()
    else dialer.onCallCompleted()
  } finally { loading.value = false }
}

async function submitCallback(stopAfter = false) {
  if (!entry.value || !cbDate.value || !cbTime.value) return
  loading.value = true
  try {
    await flushFiche()
    // Heure murale naïve (saisie de l'agent) — localisée côté serveur dans le
    // fuseau campagne › agent › global. Plus de conversion via le navigateur.
    const local = `${cbDate.value}T${cbTime.value}`
    if (rescheduleDisposition.value) {
      // Rappel issu d'une disposition de type `reschedule` : on passe par
      // complete() pour tracer `last_disposition` (le serveur pose le statut
      // callback + callback_at).
      await QueueAPI.complete(entry.value.id, {
        disposition_id: rescheduleDisposition.value.id,
        callback_at_local: local,
        notes: cbNote.value,
      })
    } else {
      await QueueAPI.callback(entry.value.id, { callback_at_local: local }, cbNote.value)
    }
    await agentStore.change('available').catch(() => {})
    reset()
    callStore.closeDisposition()
    if (stopAfter) dialer.stop()
    else dialer.onCallCompleted()
  } finally { loading.value = false }
}

/** Liste noire : ferme la fiche + interdit tout futur appel à ce numéro
 *  (globalement). Toujours disponible, quel que soit le résultat. */
async function submitBlacklist() {
  if (!entry.value) return
  const num = entry.value.phone
  const ok = await confirmStore.ask({
    title: 'Mettre en liste noire',
    message: `Le numéro ${num} ne sera plus jamais appelé (toutes campagnes confondues). Confirmer ?`,
    kind: 'danger',
    confirmLabel: 'Liste noire',
  })
  if (!ok) return
  loading.value = true
  try {
    await flushFiche()
    await QueueAPI.blacklist(entry.value.id)
    toasts.success('Numéro mis en liste noire', num)
    await agentStore.change('available').catch(() => {})
    reset()
    callStore.closeDisposition()
    dialer.onCallCompleted()
  } finally { loading.value = false }
}

function fieldInputType(t: string): string {
  switch (t) {
    case 'number': return 'number'
    case 'date': return 'date'
    case 'time': return 'time'
    case 'datetime': return 'datetime-local'
    case 'email': return 'email'
    case 'phone': return 'tel'
    default: return 'text'
  }
}

/** Heure murale NAÏVE du RDV depuis le 1er champ date/datetime rempli
 *  ('2026-06-20T14:00' ou '2026-06-20'). La localisation (→ UTC) est faite
 *  côté serveur selon le fuseau campagne › agent › global. */
function deriveScheduledAt(): string | null {
  if (!callStore.apptForm) return null
  for (const f of callStore.apptForm.fields) {
    if (f.field_type !== 'datetime' && f.field_type !== 'date') continue
    const v = callStore.apptValues[f.key]
    if (v) return String(v)
  }
  return null
}

function reset() {
  // L'état partagé (note, fiche, RDV) est remis à zéro par
  // callStore.closeDisposition() → reset().
  mode.value = 'choose'
  outcome.value = ''
  selectedDisposition.value = null
  rescheduleDisposition.value = null
  willCallback.value = ''
  serviceRequested.value = ''
  cbDate.value = ''
  cbTime.value = ''
  cbNote.value = ''
  saleForm.value = null
  saleValues.value = {}
  saleAmount.value = ''
}
</script>

<style scoped>
/* ── Panneau ancré (remplace le composeur dans le PhonePanel) ─────────── */
.dispo {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.head {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
  background: var(--surface-hover);
  flex-shrink: 0;
}
.head h2 { margin: 0; font-size: 13.5px; font-weight: 700; }
.sub { margin: 3px 0 0; color: var(--text-muted); font-size: 12px; }
.sub b { color: var(--text); font-weight: 600; }

.body {
  padding: 12px 14px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.choice-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.choice {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 16px 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.12s;
  text-align: center;
}
.choice:hover {
  border-color: var(--primary);
  background: var(--primary-50);
}
.ci { margin-bottom: 2px; }
.ci-ok { color: var(--success); }
.ci-warn { color: var(--warning); }
.ci-danger { color: var(--danger); }
.choice-danger:hover { border-color: var(--danger); }
.choice-danger:disabled { opacity: 0.5; cursor: not-allowed; }
.ci-title { font-size: 13px; font-weight: 600; color: var(--text); }
.ci-desc { font-size: 11px; color: var(--text-muted); }

.form { display: flex; flex-direction: column; gap: 12px; }
.lbl { font-size: 12px; font-weight: 600; color: var(--text-secondary); }
.opt { color: var(--text-muted); font-weight: 400; }
.req { color: var(--danger); font-weight: 700; }

/* Indications (champs requis manquants, RDV repris de la fiche) */
.hint {
  display: flex; align-items: flex-start; gap: 6px;
  margin: 0;
  padding: 8px 10px;
  background: var(--surface-hover);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  font-size: 11.5px;
  color: var(--text-secondary);
  line-height: 1.4;
}
.hint.warn {
  background: var(--warning-bg);
  border-color: var(--warning-border);
  color: var(--warning);
}
.hint :deep(svg), .hint svg { flex-shrink: 0; margin-top: 1px; }

/* Champs personnalisés de la fiche (mini softphone uniquement) */
.custom-fields {
  display: flex; flex-direction: column; gap: 10px;
  padding: 10px; border: 1px solid var(--border); border-radius: var(--radius);
  background: var(--surface-hover);
}
.cf-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); }

/* Formulaire de VENTE inline (accent émeraude). */
.sale-box {
  display: flex; flex-direction: column; gap: 6px;
  padding: 12px; border: 1px solid #A7F3D0; border-radius: var(--radius);
  background: #ECFDF5;
}
.sale-box-head { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: #059669; }
.sale-box-head :deep(svg) { color: #059669; }
.sale-amount { max-width: 200px; font-size: 16px; font-weight: 700; }
.cf { display: flex; flex-direction: column; gap: 4px; }
.cf-check { display: flex; align-items: center; gap: 8px; font-size: 13px; cursor: pointer; }
.cf input:disabled, .cf textarea:disabled, .cf select:disabled { opacity: 0.6; cursor: not-allowed; }

/* Bloc rendez-vous */
.appt { border: 1px solid var(--primary-200); border-radius: var(--radius); padding: 10px; background: var(--primary-50); display: flex; flex-direction: column; gap: 10px; }
.appt-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.appt-title { font-size: 13px; font-weight: 700; color: var(--text); }
.appt-sub { font-size: 11px; color: var(--text-muted); }
.appt-fields { display: flex; flex-direction: column; gap: 10px; }
.appt .hint { background: var(--surface); }

/* Dispositions par campagne (boutons colorés) */
.dispo-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.dispo-btn {
  display: flex; align-items: center; gap: 7px;
  padding: 9px 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 12.5px;
  color: var(--text);
  cursor: pointer;
  transition: all 0.1s;
  text-align: left;
  min-width: 0;
}
.dispo-btn:hover { border-color: var(--border-strong); background: var(--surface-hover); }
.dispo-btn.active { font-weight: 600; }
.dispo-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.dispo-btn-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.outcomes { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.outcome {
  padding: 8px 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.1s;
  text-align: center;
}
.outcome:hover { border-color: var(--border-strong); background: var(--surface-hover); }
.outcome.active {
  background: var(--primary-100);
  border-color: var(--primary);
  color: var(--primary);
  font-weight: 600;
}

textarea, .text-input {
  padding: 9px 11px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  font-size: 13px;
  outline: none;
  font-family: inherit;
  background: var(--surface);
  color: var(--text);
  width: 100%;
  box-sizing: border-box;
}
textarea { resize: vertical; }
textarea:focus, .text-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-100);
}

/* Segmented control — yes/no/maybe */
.seg {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.seg-item {
  padding: 8px 4px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--duration-fast);
}
.seg-item:hover { border-color: var(--border-strong); background: var(--surface-hover); }
.seg-item.active {
  background: var(--primary-100);
  border-color: var(--primary);
  color: var(--primary);
  font-weight: 600;
}
.seg-item:focus-visible { outline: 2px solid var(--primary); outline-offset: 1px; }

.dt { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.dt input {
  padding: 8px 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  font-size: 12.5px;
  outline: none;
  min-width: 0;
}
.dt input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-100); }

.quick { display: flex; flex-wrap: wrap; gap: 5px; }
.quick button {
  padding: 5px 10px;
  background: var(--surface-hover);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  font-size: 12px;
  color: var(--text-secondary);
  cursor: pointer;
}
.quick button:hover {
  background: var(--primary-100);
  border-color: var(--primary);
  color: var(--primary);
}

/* Actions empilées — le panneau fait ~300 px de large. */
.actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
}

/* Session de numérotation : enregistre la fiche puis ARRÊTE le défilement
   automatique (au lieu d'enchaîner sur le prochain numéro). */
.btn-stop-session {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  font-size: 13px;
  font-weight: 600;
  color: var(--danger);
  cursor: pointer;
  transition: all var(--duration-base);
}
.btn-stop-session:hover:not(:disabled) { border-color: var(--danger); background: var(--danger-bg, #FEF2F2); }
.btn-stop-session:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-blacklist-camp {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  margin-top: 8px; width: 100%;
  padding: 8px 12px;
  background: none;
  border: 1px dashed var(--danger);
  border-radius: var(--radius);
  font-size: 12.5px; font-weight: 600; color: var(--danger);
  cursor: pointer;
}
.btn-blacklist-camp:hover:not(:disabled) { background: var(--danger-bg, #FEF2F2); }
.btn-blacklist-camp:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-text {
  display: inline-flex; align-items: center; justify-content: center; gap: 4px;
  background: none; border: none;
  color: var(--text-muted);
  font-size: 13px; font-weight: 500;
  cursor: pointer;
  padding: 6px 0;
}
.btn-text:hover { color: var(--text); }

.btn-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 14px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: var(--radius);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:hover:not(:disabled) { background: var(--primary-hover); }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.dispo-enter-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.dispo-leave-active { transition: opacity 0.12s ease, transform 0.12s ease; }
.dispo-enter-from, .dispo-leave-to { opacity: 0; transform: translateY(8px); }
</style>
